/**
 * © 2024 Little Shilling, Inc. All rights reserved.
 * Shon Little
 * Created: 2026-01-27
 */

import { useState } from 'react';

// Add local dependencies.
import { useCreateDarbyTalentShowSubmissionMutation } from '../../api/publicSlice';

// Styles
const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#ffffff',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  formCard: {
    maxWidth: '800px',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '40px',
  },
  successCard: {
    maxWidth: '600px',
    margin: '100px auto',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '60px 40px',
    textAlign: 'center',
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px',
    borderBottom: '3px solid #005394',
    paddingBottom: '20px',
  },
  mainTitle: {
    fontSize: '2.5rem',
    color: '#005394',
    margin: '0 0 10px 0',
    fontWeight: 700,
  },
  subtitle: {
    fontSize: '1.5rem',
    color: '#de1e27',
    margin: '0 0 10px 0',
    fontWeight: 500,
  },
  eventDate: {
    fontSize: '1.1rem',
    color: '#333',
    margin: '0',
    fontWeight: 400,
  },
  formTitleSection: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  formTitle: {
    fontSize: '1.3rem',
    color: '#005394',
    margin: '0 0 10px 0',
    fontWeight: 600,
  },
  deadline: {
    fontSize: '1rem',
    color: '#de1e27',
    margin: '0',
    fontWeight: 600,
  },
  smallNotes: {
    fontSize: '0.75rem',
    color: '#666',
    margin: '10px 0 0 0',
    lineHeight: 1.5,
  },
  form: {
    marginTop: '30px',
  },
  section: {
    marginTop: '30px',
    paddingTop: '20px',
    borderTop: '2px solid #e0e0e0',
  },
  sectionTitle: {
    fontSize: '1.2rem',
    color: '#005394',
    margin: '0 0 20px 0',
    fontWeight: 600,
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    color: '#333',
    fontSize: '1rem',
    fontWeight: 500,
  },
  required: {
    color: '#de1e27',
    fontWeight: 700,
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '1rem',
    border: '2px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  inputError: {
    width: '100%',
    padding: '12px',
    fontSize: '1rem',
    border: '2px solid #de1e27',
    borderRadius: '4px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    backgroundColor: '#fff5f5',
  },
  select: {
    width: '100%',
    padding: '12px',
    fontSize: '1rem',
    border: '2px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    backgroundColor: '#fff',
  },
  selectError: {
    width: '100%',
    padding: '12px',
    fontSize: '1rem',
    border: '2px solid #de1e27',
    borderRadius: '4px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    backgroundColor: '#fff5f5',
  },
  helperText: {
    fontSize: '0.9rem',
    color: '#666',
    margin: '5px 0',
    fontStyle: 'italic',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '1rem',
    color: '#333',
    cursor: 'pointer',
  },
  radio: {
    marginRight: '10px',
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    fontSize: '1rem',
    color: '#333',
    cursor: 'pointer',
  },
  checkbox: {
    marginRight: '10px',
    marginTop: '3px',
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    flexShrink: 0,
  },
  errorMessage: {
    color: '#de1e27',
    fontSize: '0.9rem',
    margin: '5px 0 0 0',
    fontWeight: 500,
  },
  submitSection: {
    marginTop: '40px',
    textAlign: 'center',
    paddingTop: '30px',
    borderTop: '2px solid #e0e0e0',
  },
  submitButton: {
    backgroundColor: '#005394',
    color: '#ffffff',
    padding: '15px 40px',
    fontSize: '1.1rem',
    fontWeight: 600,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    fontFamily: 'inherit',
  },
  successMessage: {
    fontSize: '1.1rem',
    color: '#333',
    margin: '20px 0',
    lineHeight: 1.6,
  },
};

/**
 * DarbyTalentShow component.
 * A standalone page for Darby Avenue Elementary School talent show registration.
 * This page is independent of the main Releve Studios site.
 * @example
 * return (
 *   <DarbyTalentShow />
 * )
 * @returns {React.ReactElement} component.
 */
const DarbyTalentShow = () => {
  // Set mutation hook
  const [submitDarbyTalentShowForm, { isLoading: isSubmitting, isError: isSubmitError, error: submitError }] =
    useCreateDarbyTalentShowSubmissionMutation();

  // Form state
  const [formData, setFormData] = useState({
    performerName: '',
    typeOfAct: '',
    otherActType: '',
    songFile: null,
    performanceType: 'individual',
    performWith: '',
    releaseConsent: false,
    photoConsent: false,
    volunteerAuditions: false,
    volunteerStageHand: false,
    volunteerDecorating: false,
    volunteerCleanup: false,
    volunteerOther: '',
    grade: '',
    teacher: '',
    room: '',
    phone: '',
    email: '',
    signature: '',
  });

  // Validation errors state
  const [errors, setErrors] = useState({});

  // Submission state
  const [submitted, setSubmitted] = useState(false);

  // Act type options
  const actTypes = [
    'Dance',
    'Singing',
    'Acting Scene',
    'Puppetry',
    'Poetry',
    'Piano',
    'Guitar',
    'Violin',
    'Percussion',
    'Ukulele',
    'Brass Instrument',
    'Wind Instrument',
    'Magic',
    'Juggling',
    'Yo-Yo',
    'Hula Hoop',
    'Mime',
    'Clowning',
    'Live Art',
    'Other',
  ];

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === 'file') {
      setFormData((prev) => ({ ...prev, [name]: files[0] || null }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.performerName.trim()) {
      newErrors.performerName = 'Performer name is required';
    }

    if (!formData.typeOfAct) {
      newErrors.typeOfAct = 'Please select a type of act';
    }

    if (formData.typeOfAct === 'Other' && !formData.otherActType.trim()) {
      newErrors.otherActType = 'Please specify the type of act';
    }

    if ((formData.typeOfAct === 'Dance' || formData.typeOfAct === 'Singing') && !formData.songFile) {
      newErrors.songFile = 'Please upload a music file';
    }

    if (formData.performanceType === 'group' && !formData.performWith.trim()) {
      newErrors.performWith = 'Please specify who the performer will perform with';
    }

    if (!formData.releaseConsent) {
      newErrors.releaseConsent = 'This consent is required';
    }

    if (!formData.photoConsent) {
      newErrors.photoConsent = 'This consent is required';
    }

    if (!formData.grade.trim()) {
      newErrors.grade = 'Grade is required';
    }

    if (!formData.teacher.trim()) {
      newErrors.teacher = 'Teacher name is required';
    }

    if (!formData.room.trim()) {
      newErrors.room = 'Room number is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.signature.trim()) {
      newErrors.signature = 'Parent signature is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      // Create FormData for file upload support
      const apiFormData = new FormData();

      // Map camelCase to snake_case for backend
      apiFormData.append('performer_name', formData.performerName);
      apiFormData.append('type_of_act', formData.typeOfAct);
      apiFormData.append('other_act_type', formData.otherActType || '');
      apiFormData.append('performance_type', formData.performanceType);
      apiFormData.append('perform_with', formData.performWith || '');
      apiFormData.append('release_consent', formData.releaseConsent);
      apiFormData.append('photo_consent', formData.photoConsent);
      apiFormData.append('volunteer_auditions', formData.volunteerAuditions);
      apiFormData.append('volunteer_stage_hand', formData.volunteerStageHand);
      apiFormData.append('volunteer_decorating', formData.volunteerDecorating);
      apiFormData.append('volunteer_cleanup', formData.volunteerCleanup);
      apiFormData.append('volunteer_other', formData.volunteerOther || '');
      apiFormData.append('grade', formData.grade);
      apiFormData.append('teacher', formData.teacher);
      apiFormData.append('room', formData.room);
      apiFormData.append('phone', formData.phone);
      apiFormData.append('email', formData.email);
      apiFormData.append('signature', formData.signature);

      // Add file if present
      if (formData.songFile) {
        apiFormData.append('song_file', formData.songFile);
      }

      try {
        await submitDarbyTalentShowForm(apiFormData).unwrap();
        setSubmitted(true);
      } catch (err) {
        // Display backend validation errors
        if (err.data) {
          const backendErrors = {};
          Object.keys(err.data).forEach((key) => {
            // Convert snake_case to camelCase
            const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
            backendErrors[camelKey] = Array.isArray(err.data[key]) ? err.data[key][0] : err.data[key];
          });
          setErrors(backendErrors);

          // Scroll to first error
          const firstError = document.querySelector('.error-message');
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
    } else {
      // Scroll to first error
      const firstError = document.querySelector('.error-message');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // Show success message if submitted
  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.successCard}>
          <h1 style={{ ...styles.mainTitle, color: '#005394' }}>Thank You!</h1>
          <p style={styles.successMessage}>
            Your audition form for <strong>{formData.performerName}</strong> has been successfully submitted.
          </p>
          <p style={styles.successMessage}>We&apos;ll be in touch soon regarding audition details.</p>
          <button
            type="button"
            onClick={() => {
              setSubmitted(false);
              setFormData({
                performerName: '',
                typeOfAct: '',
                otherActType: '',
                songFile: null,
                performanceType: 'individual',
                performWith: '',
                releaseConsent: false,
                photoConsent: false,
                volunteerAuditions: false,
                volunteerStageHand: false,
                volunteerDecorating: false,
                volunteerCleanup: false,
                volunteerOther: '',
                grade: '',
                teacher: '',
                room: '',
                phone: '',
                email: '',
                signature: '',
              });
            }}
            style={styles.submitButton}
          >
            Submit Another Form
          </button>
        </div>
      </div>
    );
  }

  // Render form
  return (
    <div style={styles.container}>
      <div style={styles.formCard}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.mainTitle}>Shine Like A Star</h1>
          <h2 style={styles.subtitle}>A Night of Darby Talent</h2>
          <p style={styles.eventDate}>Friday, March 20th, 2026</p>
        </div>

        <div style={styles.formTitleSection}>
          <h3 style={styles.formTitle}>Permission Slip and Audition Form</h3>
          <p style={styles.deadline}>Form must be submitted by February 12th, 2026</p>
          <p style={styles.smallNotes}>
            - Participants may leave after their audition.
            <br />- Students may participate in one act only.
          </p>
        </div>

        {/* Error Alert */}
        {isSubmitError && (
          <div
            style={{
              padding: '15px',
              marginBottom: '20px',
              backgroundColor: '#fee',
              border: '1px solid #de1e27',
              borderRadius: '4px',
              color: '#de1e27',
            }}
          >
            <strong>Error:</strong>{' '}
            {submitError?.data?.message || 'There was an error submitting the form. Please try again.'}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Performer's Name */}
          <div style={styles.formGroup}>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="performerName" style={styles.label}>
              Performer&apos;s Name <span style={styles.required}>*</span>
            </label>
            <input
              id="performerName"
              type="text"
              name="performerName"
              value={formData.performerName}
              onChange={handleChange}
              style={errors.performerName ? styles.inputError : styles.input}
            />
            {errors.performerName && (
              <p style={styles.errorMessage} className="error-message">
                {errors.performerName}
              </p>
            )}
          </div>

          {/* Type of Act */}
          <div style={styles.formGroup}>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="typeOfAct" style={styles.label}>
              Type of Act <span style={styles.required}>*</span>
            </label>
            <p style={styles.helperText}>
              All acts and music must be G-Rated. Act length should be 1-3 minutes, not to exceed 3 minutes. Music must
              be provided before audition date.
            </p>
            <select
              id="typeOfAct"
              name="typeOfAct"
              value={formData.typeOfAct}
              onChange={handleChange}
              style={errors.typeOfAct ? styles.selectError : styles.select}
            >
              <option value="">Select an act type...</option>
              {actTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            {errors.typeOfAct && (
              <p style={styles.errorMessage} className="error-message">
                {errors.typeOfAct}
              </p>
            )}
          </div>

          {/* Other Act Type (conditional) */}
          {formData.typeOfAct === 'Other' && (
            <div style={styles.formGroup}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="otherActType" style={styles.label}>
                Other <span style={styles.required}>*</span>
              </label>
              <input
                id="otherActType"
                type="text"
                name="otherActType"
                value={formData.otherActType}
                onChange={handleChange}
                placeholder="Please specify the type of act"
                style={errors.otherActType ? styles.inputError : styles.input}
              />
              {errors.otherActType && (
                <p style={styles.errorMessage} className="error-message">
                  {errors.otherActType}
                </p>
              )}
            </div>
          )}

          {/* Song File (conditional) */}
          {(formData.typeOfAct === 'Dance' || formData.typeOfAct === 'Singing') && (
            <div style={styles.formGroup}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="songFile" style={styles.label}>
                Song File <span style={styles.required}>*</span>
              </label>
              <input
                id="songFile"
                type="file"
                name="songFile"
                accept=".mp3,.wav,.m4a,.aac"
                onChange={handleChange}
                style={styles.input}
              />
              <p style={styles.helperText}>Accepted formats: .mp3, .wav, .m4a, .aac</p>
              {errors.songFile && (
                <p style={styles.errorMessage} className="error-message">
                  {errors.songFile}
                </p>
              )}
            </div>
          )}

          {/* Consent Section */}
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>Consent</h4>

            <div style={styles.formGroup}>
              <span style={styles.label}>
                Performance Type <span style={styles.required}>*</span>
              </span>
              <div style={styles.radioGroup}>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label style={styles.radioLabel}>
                  <input
                    id="performanceIndividual"
                    type="radio"
                    name="performanceType"
                    value="individual"
                    checked={formData.performanceType === 'individual'}
                    onChange={handleChange}
                    style={styles.radio}
                  />
                  Performer will perform individually
                </label>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label style={styles.radioLabel}>
                  <input
                    id="performanceGroup"
                    type="radio"
                    name="performanceType"
                    value="group"
                    checked={formData.performanceType === 'group'}
                    onChange={handleChange}
                    style={styles.radio}
                  />
                  Performer will perform with:
                </label>
              </div>
            </div>

            {formData.performanceType === 'group' && (
              <div style={styles.formGroup}>
                <input
                  id="performWith"
                  type="text"
                  name="performWith"
                  value={formData.performWith}
                  onChange={handleChange}
                  placeholder="Enter names of other performers"
                  style={errors.performWith ? styles.inputError : styles.input}
                />
                {errors.performWith && (
                  <p style={styles.errorMessage} className="error-message">
                    {errors.performWith}
                  </p>
                )}
              </div>
            )}

            <div style={styles.formGroup}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label style={styles.checkboxLabel}>
                <input
                  id="releaseConsent"
                  type="checkbox"
                  name="releaseConsent"
                  checked={formData.releaseConsent}
                  onChange={handleChange}
                  style={styles.checkbox}
                />
                <span>
                  I agree my child will be released to the yard when auditions or rehearsals are complete.{' '}
                  <span style={styles.required}>*</span>
                </span>
              </label>
              {errors.releaseConsent && (
                <p style={styles.errorMessage} className="error-message">
                  {errors.releaseConsent}
                </p>
              )}
            </div>

            <div style={styles.formGroup}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label style={styles.checkboxLabel}>
                <input
                  id="photoConsent"
                  type="checkbox"
                  name="photoConsent"
                  checked={formData.photoConsent}
                  onChange={handleChange}
                  style={styles.checkbox}
                />
                <span>
                  I agree to my child being photographed for school use (yearbook, display case, etc.).{' '}
                  <span style={styles.required}>*</span>
                </span>
              </label>
              {errors.photoConsent && (
                <p style={styles.errorMessage} className="error-message">
                  {errors.photoConsent}
                </p>
              )}
            </div>
          </div>

          {/* Parent Volunteer Section */}
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>Parent Volunteer</h4>
            <p style={styles.helperText}>Please check all that apply (optional)</p>

            <div style={styles.formGroup}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label style={styles.checkboxLabel}>
                <input
                  id="volunteerAuditions"
                  type="checkbox"
                  name="volunteerAuditions"
                  checked={formData.volunteerAuditions}
                  onChange={handleChange}
                  style={styles.checkbox}
                />
                <span>Help supervise students during any of the auditions</span>
              </label>
            </div>

            <div style={styles.formGroup}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label style={styles.checkboxLabel}>
                <input
                  id="volunteerStageHand"
                  type="checkbox"
                  name="volunteerStageHand"
                  checked={formData.volunteerStageHand}
                  onChange={handleChange}
                  style={styles.checkbox}
                />
                <span>Stage hand during show; help facilitate correct order, move props, etc.</span>
              </label>
            </div>

            <div style={styles.formGroup}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label style={styles.checkboxLabel}>
                <input
                  id="volunteerDecorating"
                  type="checkbox"
                  name="volunteerDecorating"
                  checked={formData.volunteerDecorating}
                  onChange={handleChange}
                  style={styles.checkbox}
                />
                <span>Contribute with Stage and Auditorium decorating</span>
              </label>
            </div>

            <div style={styles.formGroup}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label style={styles.checkboxLabel}>
                <input
                  id="volunteerCleanup"
                  type="checkbox"
                  name="volunteerCleanup"
                  checked={formData.volunteerCleanup}
                  onChange={handleChange}
                  style={styles.checkbox}
                />
                <span>Tear down and clean up after show</span>
              </label>
            </div>

            <div style={styles.formGroup}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="volunteerOther" style={styles.label}>
                Other area of contribution
              </label>
              <input
                id="volunteerOther"
                type="text"
                name="volunteerOther"
                value={formData.volunteerOther}
                onChange={handleChange}
                placeholder="Specify other ways you'd like to help"
                style={styles.input}
              />
            </div>
          </div>

          {/* Student Information */}
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>Student Information</h4>

            <div style={styles.formGroup}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="grade" style={styles.label}>
                Grade <span style={styles.required}>*</span>
              </label>
              <input
                id="grade"
                type="text"
                name="grade"
                value={formData.grade}
                onChange={handleChange}
                style={errors.grade ? styles.inputError : styles.input}
              />
              {errors.grade && (
                <p style={styles.errorMessage} className="error-message">
                  {errors.grade}
                </p>
              )}
            </div>

            <div style={styles.formGroup}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="teacher" style={styles.label}>
                Teacher <span style={styles.required}>*</span>
              </label>
              <input
                id="teacher"
                type="text"
                name="teacher"
                value={formData.teacher}
                onChange={handleChange}
                style={errors.teacher ? styles.inputError : styles.input}
              />
              {errors.teacher && (
                <p style={styles.errorMessage} className="error-message">
                  {errors.teacher}
                </p>
              )}
            </div>

            <div style={styles.formGroup}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="room" style={styles.label}>
                Room <span style={styles.required}>*</span>
              </label>
              <input
                id="room"
                type="text"
                name="room"
                value={formData.room}
                onChange={handleChange}
                style={errors.room ? styles.inputError : styles.input}
              />
              {errors.room && (
                <p style={styles.errorMessage} className="error-message">
                  {errors.room}
                </p>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>Contact Information</h4>

            <div style={styles.formGroup}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="phone" style={styles.label}>
                Phone # <span style={styles.required}>*</span>
              </label>
              <input
                id="phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
                style={errors.phone ? styles.inputError : styles.input}
              />
              {errors.phone && (
                <p style={styles.errorMessage} className="error-message">
                  {errors.phone}
                </p>
              )}
            </div>

            <div style={styles.formGroup}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="email" style={styles.label}>
                Email <span style={styles.required}>*</span>
              </label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="parent@example.com"
                style={errors.email ? styles.inputError : styles.input}
              />
              {errors.email && (
                <p style={styles.errorMessage} className="error-message">
                  {errors.email}
                </p>
              )}
            </div>
          </div>

          {/* Parent Signature */}
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>Parent Signature</h4>

            <div style={styles.formGroup}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="signature" style={styles.label}>
                Signature <span style={styles.required}>*</span>
              </label>
              <p style={styles.helperText}>
                By typing your full name below, you agree that this constitutes your legal signature and you consent to
                all terms outlined in this form.
              </p>
              <input
                id="signature"
                type="text"
                name="signature"
                value={formData.signature}
                onChange={handleChange}
                placeholder="Type your full name"
                style={errors.signature ? styles.inputError : styles.input}
              />
              {errors.signature && (
                <p style={styles.errorMessage} className="error-message">
                  {errors.signature}
                </p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div style={styles.submitSection}>
            <button
              type="submit"
              style={{
                ...styles.submitButton,
                opacity: isSubmitting ? 0.6 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Registration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Export component.
export default DarbyTalentShow;
